import { useState, useRef } from 'react';
import * as Sentry from '@sentry/react';
import {
  parseCsvText,
  type CsvParseResult as PureCsvParseResult,
  type RawCsvRow,
} from '../utils/csvParse';
import {
  STOCKTAKE_COLUMN_ALIASES,
} from '../utils/stocktakeCsv';
import { downloadCsvFile } from '@/lib/download';

export type { RawCsvRow };

export interface CsvParseResult<T> {
  data: T[];
  errors: string[];
  rowCount: number;
}

interface UseCsvParserOptions<T> {
  requiredColumns: string[];
  onParse?: (rows: T[]) => void;
  validateRow?: (row: RawCsvRow, index: number) => { valid: boolean; error?: string };
  transformRow?: (row: RawCsvRow, index: number) => T;
  /**
   * OPT-IN parsing behaviour. Every one of these defaults to off, so the
   * pre-existing parsers in this file (SKU upload, thresholds) and
   * `usePurchaseOrderCsvParser` parse byte-identically to before.
   *
   * ⚠ `columnAliases` in particular must never be made global: those parsers
   * read snake_case row keys (`row.production_type`, `row.current_stock`,
   * `row.image_url`, `row.order_quantity`) that an aliasing/underscore-folding
   * pass would destroy.
   */
  columnAliases?: Record<string, string>;
  numericColumns?: string[];
  rejectHeaders?: Array<{ header: string; reason: string }>;
  padShortRows?: boolean;
  skipBlankRows?: boolean;
}

/**
 * Custom hook for parsing CSV files with validation and error handling
 * Provides reusable CSV parsing logic for inventory modals
 */
export function useCsvParser<T = RawCsvRow>(options: UseCsvParserOptions<T>) {
  const [file, setFile] = useState<File | null>(null);
  const [parsedData, setParsedData] = useState<T[]>([]);
  const [parseErrors, setParseErrors] = useState<string[]>([]);
  const [isParsing, setIsParsing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const {
    requiredColumns,
    onParse,
    validateRow,
    transformRow,
    columnAliases,
    numericColumns,
    rejectHeaders,
    padShortRows,
    skipBlankRows,
  } = options;

  /**
   * Last full parse result, including the structured error codes and the raw
   * cell text. Consumers that need to tell "blank" from "junk" (the stocktake
   * wizard) read `rawRows`; everything else can ignore it.
   */
  const [lastResult, setLastResult] = useState<PureCsvParseResult<T> | null>(null);

  /**
   * Parse CSV text into structured data.
   *
   * The body now lives in the pure `parseCsvText` so it can be unit-tested
   * without a renderer. The public surface of this hook is unchanged.
   */
  const parseCSVText = (text: string): CsvParseResult<T> =>
    parseCsvText<T>(text, {
      requiredColumns,
      validateRow,
      transformRow,
      columnAliases,
      numericColumns,
      rejectHeaders,
      padShortRows,
      skipBlankRows,
    });

  /**
   * Handle file selection and parsing
   */
  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];

    if (!selectedFile) {
      resetParser();
      return;
    }

    if (!selectedFile.name.endsWith('.csv')) {
      setParseErrors(['Please upload a CSV file']);
      return;
    }

    const MAX_CSV_BYTES = 5 * 1024 * 1024; // 5 MB
    if (selectedFile.size > MAX_CSV_BYTES) {
      setParseErrors(['File is too large. Maximum size is 5 MB.']);
      return;
    }

    setFile(selectedFile);
    setIsParsing(true);
    setParseErrors([]);

    try {
      const text = await readFileAsText(selectedFile);
      const result = parseCsvText<T>(text, {
        requiredColumns,
        validateRow,
        transformRow,
        columnAliases,
        numericColumns,
        rejectHeaders,
        padShortRows,
        skipBlankRows,
      });

      setLastResult(result);
      setParsedData(result.data);
      setParseErrors(result.errors);

      if (result.errors.length === 0 && onParse) {
        onParse(result.data);
      }

      // Log to Sentry if there are parsing errors
      if (result.errors.length > 0) {
        Sentry.captureMessage('CSV parsing completed with errors', {
          level: 'warning',
          tags: {
            component: 'useCsvParser',
            operation: 'parseCSV'
          },
          extra: {
            fileName: selectedFile.name,
            fileSize: selectedFile.size,
            errorCount: result.errors.length,
            successfulRows: result.data.length,
            errors: result.errors
          }
        });
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to read file';
      setParseErrors([message]);

      Sentry.captureException(error, {
        tags: {
          component: 'useCsvParser',
          operation: 'readFile'
        },
        extra: {
          fileName: selectedFile.name,
          fileSize: selectedFile.size
        }
      });
    } finally {
      setIsParsing(false);
    }
  };

  /**
   * Read file as text using FileReader
   */
  const readFileAsText = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        const text = e.target?.result as string;
        resolve(text);
      };

      reader.onerror = () => {
        reject(new Error('Failed to read file'));
      };

      reader.readAsText(file);
    });
  };

  /**
   * Reset parser state
   */
  const resetParser = () => {
    setFile(null);
    setParsedData([]);
    setParseErrors([]);
    setLastResult(null);
    setIsParsing(false);

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  /**
   * Generate and download CSV template.
   *
   * Adds the UTF-8 BOM so Excel on Windows renders CJK instead of mojibake —
   * it previously sniffed BOM-less UTF-8 as the system codepage. Callers that
   * already prepend a BOM (the count sheet) are unaffected: `downloadCsvFile`
   * in `@/lib/download` is the canonical path and de-duplicates it.
   */
  const downloadTemplate = (templateContent: string, filename: string) => {
    downloadCsvFile(templateContent, filename);
  };

  /**
   * Trigger file input click programmatically
   */
  const triggerFileSelect = () => {
    fileInputRef.current?.click();
  };

  return {
    // State
    file,
    parsedData,
    parseErrors,
    isParsing,
    fileInputRef,

    // Structured detail from the last parse (stocktake wizard uses these to
    // tell a blank "not counted" cell from junk, and to report real file lines)
    structuredErrors: lastResult?.structuredErrors ?? [],
    rawRows: lastResult?.rawRows ?? [],
    sourceRows: lastResult?.sourceRows ?? [],

    // Actions
    handleFileChange,
    resetParser,
    downloadTemplate,
    triggerFileSelect,
    parseCSVText
  };
}

/**
 * Type-specific CSV parsers for common inventory operations
 */

export interface StockUpdateRow {
  sku: string;
  stock: number;
}

export interface ThresholdUpdateRow {
  sku: string;
  notify: number;
}

/**
 * Hook for parsing ADD-STOCK CSV files.
 *
 * Behaviour is deliberately unchanged. The only addition is `rejectHeaders`:
 * Add Stock ADDS to existing quantities, so uploading a count sheet here would
 * DOUBLE every SKU at the location behind a green success toast — and the two
 * "Upload CSV..." menu items sit four lines apart in the same dropdown.
 */
export function useStockUpdateCsvParser() {
  return useCsvParser<StockUpdateRow>({
    requiredColumns: ['sku', 'stock'],
    rejectHeaders: [
      {
        header: 'stock count',
        reason:
          'This looks like a stocktake count sheet. Add Stock ADDS to current quantities. Uploading a count sheet here would double your stock. Use Stocktake → Upload CSV instead.',
      },
      {
        header: '库存数量',
        reason:
          'This looks like a stocktake count sheet. Add Stock ADDS to current quantities. Uploading a count sheet here would double your stock. Use Stocktake → Upload CSV instead.',
      },
    ],
    validateRow: (row, _index) => {
      if (!row.sku) {
        return { valid: false, error: 'Missing SKU' };
      }
      if (row.stock === undefined || row.stock === null) {
        return { valid: false, error: 'Missing stock value' };
      }
      const stockValue = typeof row.stock === 'number' ? row.stock : Number(row.stock);
      if (Number.isNaN(stockValue)) {
        return { valid: false, error: 'Stock must be a whole number' };
      }
      if (stockValue < 0) {
        return { valid: false, error: 'Stock cannot be negative' };
      }
      return { valid: true };
    },
    transformRow: (row) => ({
      sku: String(row.sku ?? ''),
      stock: typeof row.stock === 'number' ? row.stock : Number(row.stock)
    })
  });
}

export interface StocktakeCsvRow {
  sku: string;
  /** NaN when the cell was blank (not counted) or unparseable (an error). */
  stock: number;
  /** Display name — human-readable, editable, and NOT authoritative. */
  owner: string;
  /** Organization UUID. Authoritative when present; wins over `owner`. */
  ownerId: string;
  location: string;
}

/**
 * Hook for parsing STOCKTAKE count sheets.
 *
 * Forked from `useStockUpdateCsvParser` rather than parameterising it: that
 * hook serves BOTH the Add Stock and Stocktake wizards, so any option added to
 * it lands in both. These options must never reach Add Stock.
 *
 * - `numericColumns: ['stock']` — strict parse. Blank/junk yields NaN, NEVER 0.
 *   The legacy `isNaN ? 0` coercion silently set SKUs to zero.
 * - `padShortRows` — Excel drops trailing empty cells on re-save, and `Color`
 *   is legitimately empty for most Trim SKUs. Safe because every machine-read
 *   column (`sku`, `stock`) is leftmost.
 * - `skipBlankRows` — `,,,,,,` filler records would otherwise fail "Missing
 *   SKU" and block the entire file.
 *
 * NOTE: no `validateRow` negative/NaN check here. Those are per-ROW decisions
 * the wizard makes with the raw cell text (blank = not counted vs junk =
 * error); rejecting at parse time would collapse that distinction.
 */
export function useStocktakeCsvParser() {
  return useCsvParser<StocktakeCsvRow>({
    requiredColumns: ['sku', 'stock'],
    columnAliases: STOCKTAKE_COLUMN_ALIASES,
    numericColumns: ['stock'],
    padShortRows: true,
    skipBlankRows: true,
    validateRow: (row) => {
      if (!row.sku || String(row.sku).trim() === '') {
        return { valid: false, error: 'Missing SKU' };
      }
      return { valid: true };
    },
    transformRow: (row) => ({
      sku: String(row.sku ?? '').trim(),
      stock: typeof row.stock === 'number' ? row.stock : Number(row.stock),
      owner: String(row.owner ?? '').trim(),
      ownerId: String(row.ownerId ?? '').trim(),
      location: String(row.location ?? '').trim(),
    }),
  });
}

/**
 * Hook for parsing threshold update CSV files
 */
export function useThresholdUpdateCsvParser() {
  return useCsvParser<ThresholdUpdateRow>({
    requiredColumns: ['sku', 'notify'],
    validateRow: (row, _index) => {
      if (!row.sku) {
        return { valid: false, error: 'Missing SKU' };
      }
      if (row.notify === undefined || row.notify === null) {
        return { valid: false, error: 'Missing notify threshold value' };
      }
      const notifyValue = typeof row.notify === 'number' ? row.notify : Number(row.notify);
      if (notifyValue < 0) {
        return { valid: false, error: 'Notify threshold cannot be negative' };
      }
      return { valid: true };
    },
    transformRow: (row) => ({
      sku: String(row.sku ?? ''),
      notify: typeof row.notify === 'number' ? row.notify : Number(row.notify)
    })
  });
}
